import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, X, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_videogist-3/artifacts/809w15is_summbee%20logo_just%20bee.png";

const PLANS = [
  {
    id: "free",
    name: "Free",
    price: { monthly: 0, yearly: 0 },
    videos: 5,
    savings: { amount: 0, percentage: 0, monthsFree: 0 },
    features: [
      { text: "5 extractions/month", included: true },
      { text: "Basic summaries", included: true },
      { text: "All platforms supported", included: true },
      { text: "Complete transcription", included: false },
      { text: "Structured summaries", included: false },
      { text: "Export capabilities", included: false },
      { text: "Video library & organization", included: false },
      { text: "Priority processing", included: false }
    ]
  },
  {
    id: "starter",
    name: "Starter",
    price: { monthly: 7, yearly: 70 },
    videos: 50,
    popular: false,
    savings: { amount: 14, percentage: 17, monthsFree: 2 },
    features: [
      { text: "Everything in Free", included: true, highlight: true },
      { text: "50 extractions/month", included: true },
      { text: "Complete transcription", included: true },
      { text: "Structured summaries", included: true },
      { text: "PDF/TXT export", included: true },
      { text: "Email support", included: true },
      { text: "Video library & organization", included: false },
      { text: "Projects & Notion integration", included: false },
      { text: "Priority processing", included: false }
    ]
  },
  {
    id: "pro",
    name: "Pro",
    price: { monthly: 15, yearly: 144 },
    videos: 200,
    popular: true,
    savings: { amount: 36, percentage: 20, monthsFree: 2.4 },
    features: [
      { text: "Everything in Starter", included: true, highlight: true },
      { text: "200 extractions/month", included: true },
      { text: "Custom extraction depth", included: true },
      { text: "Video library & organization", included: true },
      { text: "Projects & topic management", included: true },
      { text: "Team workspaces (3 workspaces, 10 members each)", included: true },
      { text: "Save to Notion integration", included: true },
      { text: "Priority processing", included: true },
      { text: "Priority support", included: true },
      { text: "API access", included: false },
      { text: "Batch processing", included: false }
    ]
  },
  {
    id: "creator",
    name: "Enterprise",
    price: { monthly: 29, yearly: 279 },
    videos: "Unlimited",
    savings: { amount: 69, percentage: 20, monthsFree: 2.4 },
    features: [
      { text: "Everything in Pro", included: true, highlight: true },
      { text: "Unlimited extractions", included: true },
      { text: "Unlimited team workspaces & members", included: true },
      { text: "API access (100 calls/day) - Coming Soon", included: true },
      { text: "Batch processing (100 videos) - Coming Soon", included: true },
      { text: "Webhook integrations - Coming Soon", included: true },
      { text: "Dedicated support", included: true }
    ]
  }
];

function PricingPage() {
  const { user, subscription } = useContext(AuthContext);
  const navigate = useNavigate();
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [loading, setLoading] = useState(null);
  
  const handleSelectPlan = async (planId) => {
    if (planId === "free") {
      toast.info("You're on the free tier");
      return;
    }
    
    if (!user) {
      navigate("/login");
      return;
    }
    
    setLoading(planId);
    
    try {
      const response = await axios.post(
        `${API}/subscription/create-checkout`,
        {
          plan_type: planId,
          billing_cycle: billingCycle
        },
        { withCredentials: true }
      );
      
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to create checkout session");
      setLoading(null);
    }
  };
  
  return (
    <div className="pricing-page">
      <nav className="navbar">
        <div className="nav-content">
          <div className="nav-logo" onClick={() => navigate("/")} style={{cursor: "pointer"}}>
            <img src={LOGO_URL} alt="SummBee" className="logo-image" />
            <span>SummBee</span>
          </div>
          <div className="nav-links">
            <button onClick={() => navigate("/")} className="nav-link">
              <ArrowLeft size={16} /> Back to Home
            </button>
          </div>
        </div>
      </nav>
      
      <div className="pricing-container">
        <div className="pricing-header">
          <h1 className="pricing-title">Select Your Tier</h1>
          <p className="pricing-subtitle">Scale your knowledge extraction capabilities</p>
          
          <div className="billing-toggle">
            <button
              className={`billing-option ${billingCycle === "monthly" ? "active" : ""}`}
              onClick={() => setBillingCycle("monthly")}
              data-testid="monthly-billing-toggle"
            >
              Monthly
            </button>
            <button
              className={`billing-option ${billingCycle === "yearly" ? "active" : ""}`}
              onClick={() => setBillingCycle("yearly")}
              data-testid="yearly-billing-toggle"
            >
              Annual {billingCycle === "yearly" && <span className="savings-badge">Save up to 20%</span>}
            </button>
          </div>
        </div>
        
        <div className="pricing-grid">
          {PLANS.map((plan) => (
            <Card 
              key={plan.id} 
              className={`pricing-card ${plan.popular ? "popular" : ""}`}
              data-testid={`plan-card-${plan.id}`}
            >
              {plan.popular && <div className="popular-badge">RECOMMENDED</div>}
              <CardHeader>
                <CardTitle>{plan.name}</CardTitle>
                <div className="price">
                  <span className="price-amount">
                    ${plan.price[billingCycle]}
                  </span>
                  {plan.id !== "free" && (
                    <span className="price-period">/{billingCycle === "monthly" ? "mo" : "yr"}</span>
                  )}
                </div>
                {billingCycle === "yearly" && plan.savings.amount > 0 && (
                  <div className="savings-info" data-testid={`savings-${plan.id}`}>
                    <div className="savings-normal-price">
                      Normally ${plan.price.monthly * 12}/year
                    </div>
                    <div className="savings-highlight">
                      Save ${plan.savings.amount} ({plan.savings.percentage}% off)
                    </div>
                    <div className="savings-pitch">
                      Get {plan.savings.monthsFree} months free
                    </div>
                  </div>
                )}
                <CardDescription>
                  {typeof plan.videos === "number" ? `${plan.videos} extractions` : plan.videos} per month
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="features-list">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className={`${feature.included ? "included" : "excluded"} ${feature.highlight ? "highlight" : ""}`}>
                      {feature.included ? (
                        <Check className="feature-icon" />
                      ) : (
                        <X className="feature-icon" />
                      )}
                      <span className={feature.highlight ? "highlight-text" : ""}>
                        {feature.text}
                      </span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={loading === plan.id}
                  className={`plan-button ${plan.popular ? "popular" : ""}`}
                  data-testid={`select-plan-${plan.id}`}
                >
                  {loading === plan.id ? "Processing..." : plan.id === "free" ? "Current Tier" : "Select Plan"}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
        
        {subscription?.in_trial && (
          <div className="trial-notice">
            <p>
              Trial Status: {subscription.trial_videos_used} of 5 Pro-tier extractions used.
              Upgrade to continue accessing advanced features.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default PricingPage;
